import React, { useState } from 'react';
import plusIcon from '/assets/icons/plus.svg';
import minusIcon from '/assets/icons/minus.svg';
import helpcircleIcon from '/assets/icons/helpcircle.svg';

const faqData = [
    {
        id: 1,
        question: "What types brands can partner with Travel Rethink Ways?",
        answer: "We collaborate with travel and tourism-focused businesses including destinations, tourism boards, hotels, resorts, travel agencies, DMCs, hospitality brands, and travel startups."
    },
    {
        id: 2,
        question: "What collaboration formats do you offer?",
        answer: "We offer multiple visibility opportunities including brand story features, sponsored travel content, social media campaigns, web stories, destination campaigns, homepage placements, and strategic banner visibility."
    },
    {
        id: 3,
        question: "Can campaigns be customized based on our goals?",
        answer: "Yes. Every collaboration can be tailored based on your campaign objectives, target audience, destination focus, and preferred visibility formats."
    },
    {
        id: 4,
        question: "Do you support destination and tourism campaigns?",
        answer: "Yes. We collaborate with destinations, tourism boards, and regional travel campaigns through multi-format travel visibility and storytelling opportunities."
    },
    {
        id: 5,
        question: "Do you offer social media promotions?",
        answer: "Yes. We create travel-focused social media campaigns including reels, stories, curated posts, and promotional visibility across selected platforms."
    },
    {
        id: 6,
        question: "Can hotels and resorts get featured separately?",
        answer: "Absolutely. We work with hotels, resorts, luxury stays, and hospitality brands through dedicated travel features, campaign visibility, and curated placements."
    },
    {
        id: 7,
        question: "Are banner placements available across the website?",
        answer: "Yes. Strategic banner placements are available across selected pages, categories, campaigns, and high-visibility sections of the platform."
    },
    {
        id: 8,
        question: "Do you accept sponsored travel content?",
        answer: "Yes. We work with travel brands and tourism businesses on sponsored editorial content aligned with our travel-focused audience and platform style."
    },
    {
        id: 9,
        question: "Do you work with selected travel brands only?",
        answer: "We collaborate with travel & tourism brands aligned with our platform, audience experience, and long-term vision."
    },
    {
        id: 10,
        question: "How do we get started?",
        answer: "Simply submit the partnership form or connect with us to discuss your campaign goals and collaboration requirements."
    }
];

const FAQSection = () => {
    // State to track which FAQ is currently open
    const [openId, setOpenId] = useState(null);

    const toggleFAQ = (id) => {
        // If clicking the currently open FAQ, close it. Otherwise, open the new one.
        setOpenId(openId === id ? null : id);
    };

    // Split the data into two arrays for independent columns
    // Left column gets odd indexes (1st, 3rd, 5th), Right column gets even indexes (2nd, 4th, 6th)
    const leftColumnFaqs = faqData.filter((_, index) => index % 2 === 0);
    const rightColumnFaqs = faqData.filter((_, index) => index % 2 !== 0);

    // Use the same working WhatsApp business number as the rest of the site.
    const whatsappNumber = "9184385006813";
    const whatsappMessage = encodeURIComponent("Hi Travel Rethink Ways \n\nI'm interested in exploring partnership opportunities with your platform.");
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`;

    // Helper function to render a single FAQ card to keep the code clean
    const renderFaqCard = (faq) => {
        const isOpen = openId === faq.id;

        return (
            <div
                key={faq.id}
                className={`w-full rounded-2xl border bg-black transition-all duration-300 ease-in-out overflow-hidden ${isOpen ? 'border-gray-600 shadow-lg' : 'border-white/10 hover:border-white/30'} `}
            >
                {/* Accordion Button */}
                <button
                    onClick={() => toggleFAQ(faq.id)}
                    className="w-full flex items-center justify-between p-6 sm:p-7 text-left focus:outline-none group bg-transparent active:bg-transparent focus:bg-transparent"
                >
                    <span className="text-base sm:text-lg lg:text-[22px] font-medium pr-4 text-white transition-colors">
                        {faq.question}
                    </span>

                    {/* Animated Plus/Minus Icon */}
                    <div className={`
                        flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full transition-transform duration-300
                        ${isOpen ? 'rotate-180' : 'group-hover:text-gray-300'}
                    `}>
                        <img src={isOpen ? minusIcon : plusIcon} alt="toggle" className="w-6 h-6" style={{ filter: 'brightness(0) invert(1)' }} />
                    </div>
                </button>

                {/* Accordion Content (Smooth height animation using CSS Grid) */}
                <div
                    className={`
                        grid transition-all duration-300 ease-in-out
                        ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}
                    `}
                >
                    <div className="overflow-hidden">
                        <div className="p-6 sm:p-7 pt-0 text-white text-base sm:text-lg lg:text-[18px] leading-relaxed">
                            {faq.answer}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <section className="relative w-full bg-[#000000] py-5 sm:py-8 px-4 sm:px-8 font-sans overflow-hidden">

            {/* --- Content Container --- */}
            <div className="relative z-10 max-w-[1500px] mx-auto flex flex-col items-center">

                {/* Section Header */}
                <div className="flex flex-col items-center text-center mb-16">

                    {/* Small Pill Label */}
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#d02525] border border-gray-800 mb-6 shadow-sm">
                        {/* <img src={helpcircleIcon} alt="help" className="w-3.5 h-3.5" /> */}
                        <span className="text-white font-poppins text-[10px] sm:text-xs md:text-sm font-semibold tracking-widest uppercase">
                            Frequently Asked Questions
                        </span>
                    </div>

                    {/* Main Title */}
                    <h2 className="text-2xl sm:text-5xl md:text-4xl lg:text-5xl xl:text-[54px] font-semiboldmedium text-white tracking-tight mb-1 leading-tight whitespace-normal">
                        Common Partnership Questions
                    </h2>
                    {/* <div className="w-20 sm:w-32 md:w-40 lg:w-20 h-1 bg-[#d02525] mt-0 mx-auto" aria-hidden="true"></div> */}

                    {/* Subtitle */}
                    <p className="text-white text-[18px] sm:text-lg md:text-xl lg:text-xl xl:text-2xl font-poppins font-medium whitespace-normal max-w-4xl leading-relaxed mt-2 sm:mt-2">
                        Answers to common questions about campaign, collaborations, brand visibility, and travel partnerships with Travel Rethink Ways.
                    </p>
                </div>

                {/* Two Independent Columns Layout */}
                <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 items-start">

                    {/* Left Column */}
                    <div className="flex flex-col gap-4 sm:gap-6 w-full font-poppins">
                        {leftColumnFaqs.map(renderFaqCard)}
                    </div>

                    {/* Right Column */}
                    <div className="flex flex-col gap-4 sm:gap-6 w-full font-poppins">
                        {rightColumnFaqs.map(renderFaqCard)}
                    </div>

                </div>


            </div>
        </section>
    );
};

export default FAQSection;